import {
  CheckoutElementsProvider,
  ExpressCheckoutElement,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps, FormEvent, ReactNode } from 'react';
import type { CheckoutPaymentMethod, CheckoutStatus, ProductView } from './types';

type LoadState = 'loading' | 'ready' | 'error';
type CheckoutStep = 'review' | 'payment';
type ExpressConfirmEvent = Parameters<NonNullable<ComponentProps<typeof ExpressCheckoutElement>['onConfirm']>>[0];

const Shield = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2 4 5v6c0 5.1 3.4 9.9 8 11 4.6-1.1 8-5.9 8-11V5l-8-3Zm3.7 7.8-4.3 4.4a1 1 0 0 1-1.4 0l-1.8-1.8 1.4-1.4 1.1 1.1 3.6-3.7 1.4 1.4Z" /></svg>
);

const Tick = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.2 4.2L19 6.5" /></svg>
);

const CardIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm0 3v2h18V8H3Zm3 6v2h5v-2H6Z" /></svg>
);

const BankIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2 10 5v2H2V7l10-5ZM4 11h3v7H4v-7Zm6 0h4v7h-4v-7Zm7 0h3v7h-3v-7ZM2 20h20v2H2v-2Z" /></svg>
);

function Layout({ children, brand }: { children: ReactNode; brand?: string }) {
  return (
    <div className="page-shell">
      <header className="topbar">
        <a className="identity" href="/" aria-label="JA Group Services Shared Checkout">
          <span className="monogram">JA</span>
          <span><strong>Central Payments</strong><small>Shared Checkout</small></span>
        </a>
        <span className="secure-label"><Shield /> Secure payment service</span>
      </header>
      <main>
        {brand && <p className="brand-context">Service <strong>{brand}</strong></p>}
        {children}
      </main>
      <footer>
        <div><strong>JA Group Services Ltd</strong><br />Company number 16314179 · Registered in England and Wales</div>
        <nav aria-label="Checkout information">
          <a href="https://www.jagroupservices.co.uk/privacy" rel="noreferrer">Privacy</a>
          <a href="https://www.jagroupservices.co.uk/terms" rel="noreferrer">Terms</a>
          <a href="https://www.jagroupservices.co.uk/contact" rel="noreferrer">Help</a>
        </nav>
        <p>Payment details are securely collected by Stripe. JA Group Services Ltd does not receive or store your complete card or bank account details.</p>
      </footer>
    </div>
  );
}

function Message({ title, children }: { title: string; children: ReactNode }) {
  return <section className="message-card" role="alert"><span className="message-icon">!</span><div><h1>{title}</h1><p>{children}</p></div></section>;
}

const brandNames: Record<string, string> = {
  JA_GROUP_SERVICES: 'JA Group Services',
  SOUSA_MURRAY_SITES: 'Sousa Murray Sites',
  SOUSA_MURRAY_PROFILES: 'Sousa Murray Profiles',
  SOUSA_MURRAY_ELEARNING: 'Sousa Murray eLearning',
  SOUSA_MURRAY_FITNESS: 'Sousa Murray Fitness',
  SOUSA_MURRAY_REWARDS: 'Sousa Murray Rewards',
  SOUSA_MURRAY_ESIGNING: 'Sousa Murray e-Sign',
  SOUSA_MURRAY_ROADSIDE: 'Sousa Murray Roadside Assistance',
  SOUSA_MURRAY_COURIER: 'Sousa Murray Courier',
};

const brandDestinations: Record<string, string> = {
  JA_GROUP_SERVICES: 'https://jagroupservices.co.uk',
  SOUSA_MURRAY_SITES: 'https://sousamurraysites.jagroupservices.co.uk',
  SOUSA_MURRAY_PROFILES: 'https://sousamurrayprofiles.jagroupservices.co.uk',
  SOUSA_MURRAY_ELEARNING: 'https://sousamurrayelearning.jagroupservices.co.uk',
  SOUSA_MURRAY_FITNESS: 'https://sousamurrayfitness.jagroupservices.co.uk',
  SOUSA_MURRAY_REWARDS: 'https://rewards.jagroupservices.co.uk',
  SOUSA_MURRAY_ESIGNING: 'https://sousamurrayesign.jagroupservices.co.uk',
  SOUSA_MURRAY_ROADSIDE: 'https://roadside.jagroupservices.co.uk',
};

function Confirmation({ reference }: { reference: string }) {
  const [result, setResult] = useState<CheckoutStatus>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`/api/session-status?reference=${encodeURIComponent(reference)}`, { headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to confirm payment');
        setResult(await response.json() as CheckoutStatus);
      })
      .catch(() => setFailed(true));
  }, [reference]);

  if (failed) return <Layout><Message title="We couldn’t confirm the payment">Please check your email or contact Customer Services before trying again.</Message></Layout>;
  if (!result) return <Layout><div className="loading-card"><span className="spinner" />Confirming your payment securely…</div></Layout>;
  const brand = brandNames[result.brandCode] ?? 'Sousa Murray';
  if (result.status !== 'complete' || result.paymentStatus !== 'paid') {
    return <Layout brand={brand}><Message title="Payment is processing">Central Payments has received the checkout. Direct Debit and some other payment methods can take longer to confirm, so please check your email before trying again.</Message></Layout>;
  }

  return (
    <Layout brand={brand}>
      <section className="success-card">
        <div className="success-tick">✓</div>
        <p className="eyebrow">Payment confirmed</p>
        <h1>Payment complete</h1>
        <p>Your purchase has been confirmed by Central Payments and will continue through the normal Head Office fulfilment process.</p>
        <a className="primary-button" href={brandDestinations[result.brandCode] ?? 'https://jagroupservices.co.uk'}>Return to {brand}</a>
        <small>Checkout reference: {reference}</small>
      </section>
    </Layout>
  );
}

function Progress({ step }: { step: CheckoutStep }) {
  return (
    <ol className="checkout-progress" aria-label="Checkout progress">
      <li className={step === 'review' ? 'active' : 'done'}><span>1</span><div><strong>Review</strong><small>Order and method</small></div></li>
      <li className={step === 'payment' ? 'active' : ''}><span>2</span><div><strong>Payment</strong><small>Secure details</small></div></li>
      <li><span>3</span><div><strong>Complete</strong><small>Confirmation</small></div></li>
    </ol>
  );
}

function ProductSummary({ product, compact = false, paymentMethod }: { product: ProductView; compact?: boolean; paymentMethod: CheckoutPaymentMethod }) {
  const directDebit = paymentMethod === 'bacs_debit';
  return (
    <section className={compact ? 'panel order-summary compact' : 'panel order-summary'}>
      <div className="panel-header product-heading">
        <div className="product-mark">{product.brand.split(/\s+/).map((part) => part[0]).slice(0, 2).join('').toUpperCase()}</div>
        <div><p className="eyebrow">{product.brand}</p><h2>{product.name}</h2></div>
        <span className={`status-tag ${product.audience}`}>{product.audience === 'business' ? 'Business' : 'Consumer'}</span>
      </div>
      <div className="panel-body">
        <p className="product-description">{product.description}</p>
        {!compact && product.features.length > 0 && (
          <div className="included-card">
            <h3>Included</h3>
            <ul>{product.features.map((feature) => <li key={feature}><span className="feature-tick"><Tick /></span>{feature}</li>)}</ul>
          </div>
        )}
        <div className="price-breakdown">
          <div><span>{product.mode === 'subscription' ? 'Standard subscription' : 'Purchase'}</span><strong>{product.priceLabel}</strong></div>
          {directDebit && <div className="discount-row"><span>Business Direct Debit saving</span><strong>−£5.00</strong></div>}
          <div className="total"><span>{directDebit ? 'Final amount' : 'Total'}</span><strong>{directDebit ? 'Shown securely by Stripe' : product.priceLabel}</strong></div>
        </div>
      </div>
    </section>
  );
}

function PaymentMethodSelector({ product, value, onChange }: { product: ProductView; value: CheckoutPaymentMethod; onChange: (method: CheckoutPaymentMethod) => void }) {
  if (product.audience !== 'business' || product.mode !== 'subscription') {
    return (
      <section className="panel method-panel">
        <div className="panel-header"><div><p className="eyebrow">Payment method</p><h2>Standard payment</h2></div><span className="status-tag neutral">Consumer</span></div>
        <div className="panel-body method-static"><CardIcon /><div><strong>Card or supported wallet</strong><p>Pay securely by debit card, credit card, Apple Pay or Google Pay where available.</p></div></div>
      </section>
    );
  }

  return (
    <section className="panel method-panel">
      <div className="panel-header"><div><p className="eyebrow">Payment method</p><h2>Choose how the business will pay</h2></div><span className="status-tag business">Business</span></div>
      <div className="panel-body method-grid" role="radiogroup" aria-label="Business payment method">
        <button type="button" className={`method-option ${value === 'card' ? 'selected' : ''}`} onClick={() => onChange('card')} role="radio" aria-checked={value === 'card'}>
          <span className="method-icon"><CardIcon /></span>
          <span><strong>Card</strong><small>Debit/credit card and supported wallets</small></span>
          <span className="radio-dot" />
        </button>
        <button type="button" className={`method-option ${value === 'bacs_debit' ? 'selected' : ''}`} onClick={() => onChange('bacs_debit')} role="radio" aria-checked={value === 'bacs_debit'}>
          <span className="method-icon"><BankIcon /></span>
          <span><strong>Bacs Direct Debit <em>Save £5</em></strong><small>Recurring collection from a UK business bank account</small></span>
          <span className="radio-dot" />
        </button>
        {value === 'bacs_debit' && (
          <div className="method-notice"><Shield /><div><strong>Direct Debit mandate</strong><p>Stripe securely collects the bank details and creates the Bacs mandate. Until custom Bacs branding is enabled, Stripe may be shown in the mandate or banking experience as the payment collector.</p></div></div>
        )}
      </div>
    </section>
  );
}

function ReviewStep({ product, paymentMethod, onMethodChange, onContinue, busy }: {
  product: ProductView;
  paymentMethod: CheckoutPaymentMethod;
  onMethodChange: (method: CheckoutPaymentMethod) => void;
  onContinue: () => void;
  busy: boolean;
}) {
  return (
    <div className="review-layout">
      <ProductSummary product={product} paymentMethod={paymentMethod} />
      <div className="review-side">
        <PaymentMethodSelector product={product} value={paymentMethod} onChange={onMethodChange} />
        <section className="panel compact-panel">
          <div className="panel-body compact-list">
            <div><span>Billing</span><strong>{product.billingDescription}</strong></div>
            <div><span>After payment</span><strong>{product.fulfilmentDescription}</strong></div>
            <div><span>Support</span><strong>{product.supportDescription}</strong></div>
          </div>
        </section>
        <button className="checkout-action" type="button" onClick={onContinue} disabled={busy}>
          {busy ? <><span className="button-spinner" />Preparing secure payment…</> : <>{paymentMethod === 'bacs_debit' ? 'Continue to Direct Debit' : 'Continue to payment'} <span aria-hidden="true">→</span></>}
        </button>
        <p className="action-note"><Shield /> Payment details are entered only on the secure Stripe step.</p>
      </div>
    </div>
  );
}

function CheckoutPaymentForm({ product, paymentMethod, onBack }: { product: ProductView; paymentMethod: CheckoutPaymentMethod; onBack: () => void }) {
  const checkoutState = useCheckoutElements();
  const [paying, setPaying] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const directDebit = paymentMethod === 'bacs_debit';

  if (checkoutState.type === 'loading') return <div className="payment-loading"><span className="spinner" />Loading secure payment methods…</div>;
  if (checkoutState.type === 'error') return <Message title="Payment form unavailable">{checkoutState.error.message}</Message>;

  const { checkout } = checkoutState;

  async function handleResult(result: Awaited<ReturnType<typeof checkout.confirm>>) {
    if (result.type === 'error') {
      setPaymentError(result.error.message || 'Your payment could not be confirmed.');
      setPaying(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (paying) return;
    setPaymentError('');
    setPaying(true);
    await handleResult(await checkout.confirm());
  }

  async function handleExpressConfirm(event: ExpressConfirmEvent) {
    if (paying) return;
    setPaymentError('');
    setPaying(true);
    await handleResult(await checkout.confirm({ expressCheckoutConfirmEvent: event }));
  }

  return (
    <div className="payment-layout">
      <aside className="payment-order-column">
        <button className="back-button" type="button" onClick={onBack} disabled={paying}>← Back to review</button>
        <ProductSummary product={product} compact paymentMethod={paymentMethod} />
        <div className="secure-explainer"><Shield /><div><strong>{directDebit ? 'Bacs Direct Debit' : 'Secure card payment'}</strong><span>{directDebit ? 'Stripe collects the account details and creates the Direct Debit mandate securely.' : 'Card and wallet details are securely handled by Stripe.'}</span></div></div>
      </aside>
      <section className="panel payment-card" aria-label="Payment details">
        <div className="panel-header payment-heading">
          <div><p className="eyebrow">Secure payment</p><h2>{directDebit ? 'Set up Direct Debit' : 'Enter payment details'}</h2><p>{directDebit ? 'Use the UK bank account authorised to pay this business subscription.' : 'Choose a card or supported wallet to complete the purchase.'}</p></div>
          <span className="stripe-mark">Powered by <strong>stripe</strong></span>
        </div>
        <div className="panel-body">
          {!directDebit && <><div className="express-area"><ExpressCheckoutElement onConfirm={handleExpressConfirm} /></div><div className="payment-divider"><span>or pay by card</span></div></>}
          {directDebit && <div className="direct-debit-banner"><BankIcon /><div><strong>£5 Direct Debit saving applied</strong><p>The discount is controlled by Head Office and applies only while this checkout is completed using Bacs Direct Debit.</p></div></div>}
          <form onSubmit={handleSubmit} className="payment-form">
            <PaymentElement options={{ layout: 'accordion' }} />
            {paymentError && <div className="payment-error" role="alert">{paymentError}</div>}
            <button className="checkout-action pay" type="submit" disabled={paying}>
              {paying ? <><span className="button-spinner" />Confirming…</> : <>{directDebit ? 'Authorise Direct Debit' : `Pay ${product.priceLabel}`} <span aria-hidden="true">→</span></>}
            </button>
            <p className="payment-legal">{directDebit
              ? 'By continuing, you confirm that you are authorised to set up this Direct Debit and agree to the applicable service terms and Bacs mandate presented by Stripe.'
              : 'By confirming payment, you authorise the charge or subscription shown in your order summary and agree to the applicable JA Group Services terms for this service.'}</p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Checkout() {
  const [state, setState] = useState<LoadState>('loading');
  const [step, setStep] = useState<CheckoutStep>('review');
  const [product, setProduct] = useState<ProductView>();
  const [paymentMethod, setPaymentMethod] = useState<CheckoutPaymentMethod>('card');
  const [sessionPaymentMethod, setSessionPaymentMethod] = useState<CheckoutPaymentMethod>();
  const [publishableKey, setPublishableKey] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [startingPayment, setStartingPayment] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    fetch('/api/product', { headers: { Accept: 'application/json' } })
      .then(async (response) => {
        if (!response.ok) throw new Error('Product unavailable');
        const data = await response.json() as { product: ProductView };
        setProduct(data.product);
        setPaymentMethod('card');
        sessionStorage.setItem('ja_checkout_product', JSON.stringify(data.product));
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  function changePaymentMethod(method: CheckoutPaymentMethod) {
    setPaymentMethod(method);
    if (sessionPaymentMethod && sessionPaymentMethod !== method) {
      setClientSecret('');
      setPublishableKey('');
      setSessionPaymentMethod(undefined);
    }
  }

  async function beginPayment() {
    if (clientSecret && publishableKey && sessionPaymentMethod === paymentMethod) {
      setStep('payment');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (startingPayment) return;
    setStartingPayment(true);
    try {
      const checkoutResponse = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethod }),
      });
      const checkout = await checkoutResponse.json() as { clientSecret?: string; publishableKey?: string; paymentMethod?: CheckoutPaymentMethod; error?: string };
      if (!checkoutResponse.ok || !checkout.clientSecret || !checkout.publishableKey) throw new Error(checkout.error ?? 'Checkout unavailable');
      setClientSecret(checkout.clientSecret);
      setPublishableKey(checkout.publishableKey);
      setSessionPaymentMethod(checkout.paymentMethod || paymentMethod);
      setStep('payment');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setState('error');
    } finally {
      setStartingPayment(false);
    }
  }

  const stripePromise = useMemo(() => publishableKey ? loadStripe(publishableKey) : null, [publishableKey]);

  if (state === 'loading') return <Layout><div className="loading-card"><span className="spinner" />Preparing checkout…</div></Layout>;
  if (state === 'error' || !product) return <Layout><Message title="This checkout isn’t available">The secure checkout could not be started. Please return to the service and try again.</Message></Layout>;

  const appearance = {
    theme: 'stripe' as const,
    labels: 'above' as const,
    variables: {
      colorPrimary: '#315b8a',
      colorBackground: '#ffffff',
      colorText: '#243246',
      colorDanger: '#b42318',
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
      borderRadius: '8px',
      spacingUnit: '4px',
      fontSizeBase: '13px',
    },
  };

  return (
    <Layout brand={product.brand}>
      <section className="page-heading-card">
        <div><p className="eyebrow">Central Payments</p><h1>{step === 'review' ? 'Review checkout' : 'Complete payment'}</h1><p>{step === 'review' ? 'Confirm the service and approved payment route before continuing.' : 'Complete the secure payment or Direct Debit mandate below.'}</p></div>
        <span className="checkout-security"><Shield /> Head Office governed</span>
      </section>
      <Progress step={step} />

      {step === 'review' && <ReviewStep product={product} paymentMethod={paymentMethod} onMethodChange={changePaymentMethod} onContinue={beginPayment} busy={startingPayment} />}

      {step === 'payment' && stripePromise && clientSecret && sessionPaymentMethod && (
        <CheckoutElementsProvider stripe={stripePromise} options={{ clientSecret, elementsOptions: { appearance } }}>
          <CheckoutPaymentForm product={product} paymentMethod={sessionPaymentMethod} onBack={() => { setStep('review'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
        </CheckoutElementsProvider>
      )}
    </Layout>
  );
}

export function App() {
  const params = new URLSearchParams(window.location.search);
  const reference = params.get('checkout_reference');
  if (reference) return <Confirmation reference={reference} />;
  if (window.location.pathname.startsWith('/checkout')) return <Checkout />;
  return <Layout><Message title="Nothing to check out yet">Choose a product or plan on one of our customer brands, then select its checkout button.</Message></Layout>;
}
